import { useState, useEffect } from 'react';
import { Loader2, Send, Bug, Lightbulb, HelpCircle, Wrench } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'feature_request', label: 'Richiesta Funzionalità', icon: Lightbulb },
  { value: 'question', label: 'Domanda', icon: HelpCircle },
  { value: 'operational', label: 'Problema Operativo', icon: Wrench },
];

interface QuickTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  capturedUrl: string;
}

export default function QuickTicketDialog({ open, onOpenChange, capturedUrl }: QuickTicketDialogProps) {
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('low');
  const [category, setCategory] = useState('bug');
  const [urlPath, setUrlPath] = useState(capturedUrl);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setUrlPath(capturedUrl);
    }
  }, [open, capturedUrl]);

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setUrgency('low');
    setCategory('bug');
    setUrlPath('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const handleCreate = async () => {
    if (!subject.trim() || !description.trim()) {
      toast({ title: 'Errore', description: 'Compila tutti i campi obbligatori', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const data = await apiClient.post('/support', {
        subject: subject.trim(),
        description: description.trim(),
        urgency,
        category,
        url_path: urlPath || undefined,
      });
      if (data.success) {
        toast({ title: 'Ticket creato', description: 'Il tuo ticket è stato inviato con successo' });
        resetForm();
        onOpenChange(false);
      } else {
        toast({ title: 'Errore', description: data.error || 'Errore nella creazione', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile creare il ticket', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[500px] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-white">Apri Ticket di Supporto</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oggetto *</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Descrivi brevemente il problema"
              className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione *</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Fornisci tutti i dettagli utili"
              rows={5}
              className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoria</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgenza</label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Bassa</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Critica</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pagina di riferimento</label>
            <Input
              value={urlPath}
              onChange={(e) => setUrlPath(e.target.value)}
              placeholder="/pagina"
              className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </div>
          <Button
            onClick={handleCreate}
            disabled={creating || !subject.trim() || !description.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Invia Ticket
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
